import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Dashboard as DashboardIcon, Assignment, CalendarToday, Settings, Edit, CheckCircle, EmojiEvents, EventNote, Event, EventAvailable, Person, AttachMoney, PictureAsPdf, HowToReg, School, VideoCall, AddCircle } from '@mui/icons-material';
import AppShell, { NavItem, NavSubItem, NavGroup, NavDivider } from '../components/layout/AppShell';
import { internshipService } from '../services/api';
import MyInternship from '../components/MyInternship';
import { useAuth } from '../contexts/AuthContext';
import MyDashboard from '../components/MyDashboard';
import TimesheetEntry from '../components/TimesheetEntry';
import MyTimesheets from '../components/MyTimesheets';
import ChangePassword from '../components/ChangePassword';
import AttendanceTracking from '../components/AttendanceTracking';
import LeaveManagement from '../components/LeaveManagement';
import HolidayList from '../components/HolidayList';
import GoalManagement from '../components/GoalManagement';
import MyEmployeeDetails from '../components/MyEmployeeDetails';
import OnboardingForm from '../components/OnboardingForm';
import MyPayslips from '../components/MyPayslips';
import MyInterviewPanel from '../components/MyInterviewPanel';
import GenerateMeetingPage from '../components/GenerateMeetingPage';

interface DeveloperDashboardProps {
  mobileOpen?: boolean;
  onDrawerClose?: () => void;
  onDrawerToggle?: () => void;
}

const LEAVE_VIEWS = ['attendance', 'leave', 'holidays'];
const PAYROLL_VIEWS = ['payslips'];
const MEETING_VIEWS = ['generatemeeting', 'mypanel'];

const DeveloperDashboard: React.FC<DeveloperDashboardProps> = ({ mobileOpen = false, onDrawerClose, onDrawerToggle }) => {
  const { can } = useAuth();
  const navigate = useNavigate();
  const { view } = useParams<{ view?: string }>();
  const currentView = view || 'overview';
  // "My Internship" only appears for users who are (or were) interns — /internships/me is 404
  // for everyone else.
  const [isIntern, setIsIntern] = useState(false);

  useEffect(() => {
    internshipService.getMine().then(d => setIsIntern(!!d)).catch(() => setIsIntern(false));
  }, []);
  // Groups start open when the URL (on first load or refresh) points at one of their
  // views, so the sidebar reveals where you are instead of always resetting to defaults.
  const [openTimesheet, setOpenTimesheet] = useState(true);
  const [openLeave, setOpenLeave] = useState(LEAVE_VIEWS.includes(currentView));
  const [openPayroll, setOpenPayroll] = useState(PAYROLL_VIEWS.includes(currentView));
  const [openMeetings, setOpenMeetings] = useState(MEETING_VIEWS.includes(currentView));

  // Selecting a nav item navigates to its own URL (so refresh/back-forward keep the
  // right page+sidebar state) and closes the mobile drawer — on desktop (permanent
  // drawer) onDrawerClose is a no-op since mobileOpen never becomes true there.
  const selectView = (view: string) => {
    navigate(`/dashboard/${view}`);
    onDrawerClose?.();
  };

  const renderContent = () => {
    switch (currentView) {
      case 'overview': return <MyDashboard />;
      case 'entry': return <TimesheetEntry />;
      case 'mytimesheets': return <MyTimesheets />;
      case 'goals': return <GoalManagement />;
      case 'attendance': return <AttendanceTracking />;
      case 'leave': return <LeaveManagement />;
      case 'holidays': return <HolidayList />;
      case 'mydetails': return <MyEmployeeDetails />;
      case 'onboarding': return <OnboardingForm />;
      case 'payslips': return <MyPayslips />;
      case 'generatemeeting': return <GenerateMeetingPage />;
      case 'mypanel': return <MyInterviewPanel />;
      case 'myinternship': return <MyInternship />;
      case 'settings': return <ChangePassword />;
      default: return <MyDashboard />;
    }
  };

  const showLeaveSection = can('CreateLeaveRequest') || can('ViewOwnAttendance') || can('MarkAttendance') || can('ViewHolidays');
  const showPayrollSection = can('ViewOwnPayslip');

  const drawerContent = (
        <>
            {/* Personal overview — always visible, lands here by default */}
            <NavItem icon={<DashboardIcon />} label="Dashboard" active={currentView === 'overview'} onClick={() => selectView('overview')} />

            {/* Timesheet — always visible */}
            <NavGroup icon={<Assignment />} label="Timesheet" open={openTimesheet} onToggle={() => setOpenTimesheet(!openTimesheet)}>
                <NavSubItem icon={<Edit />} label="Log Time" active={currentView === 'entry'} onClick={() => selectView('entry')} />
                <NavSubItem icon={<CheckCircle />} label="My Timesheets" active={currentView === 'mytimesheets'} onClick={() => selectView('mytimesheets')} />
              </NavGroup>

            {/* Goals */}
            {can('ViewOwnGoals') && (
              <NavItem icon={<EmojiEvents />} label="My Goals" active={currentView === 'goals'} onClick={() => selectView('goals')} />
            )}

            {/* Attendance & Leave */}
            {showLeaveSection && (
              <>
                <NavGroup icon={<CalendarToday />} label="Attendance & Leave" open={openLeave} onToggle={() => setOpenLeave(!openLeave)}>
                    {(can('ViewOwnAttendance') || can('MarkAttendance')) && (
                      <NavSubItem icon={<EventNote />} label="Attendance" active={currentView === 'attendance'} onClick={() => selectView('attendance')} />
                    )}
                    {can('CreateLeaveRequest') && (
                      <NavSubItem icon={<Event />} label="Leave" active={currentView === 'leave'} onClick={() => selectView('leave')} />
                    )}
                    {can('ViewHolidays') && (
                      <NavSubItem icon={<EventAvailable />} label="Holidays" active={currentView === 'holidays'} onClick={() => selectView('holidays')} />
                    )}
                  </NavGroup>
              </>
            )}

            {/* Payroll */}
            {showPayrollSection && (
              <>
                <NavGroup icon={<AttachMoney />} label="Payroll" open={openPayroll} onToggle={() => setOpenPayroll(!openPayroll)}>
                    <NavSubItem icon={<PictureAsPdf />} label="Payslips & CTC" active={currentView === 'payslips'} onClick={() => selectView('payslips')} />
                  </NavGroup>
              </>
            )}

            {/* Meetings — Generate Meeting bundles Virtual Office presence, starting/joining a
                temporary meeting, and the join history on one page; the interview panel sits
                alongside it (always visible, not permission-gated — it shows up the moment
                HR/Admin schedules one with this employee). Moved off the Attendance page so that
                page is attendance-only. */}
            <NavGroup icon={<VideoCall />} label="Meetings" open={openMeetings} onToggle={() => setOpenMeetings(!openMeetings)}>
                <NavSubItem icon={<AddCircle />} label="Generate Meeting" active={currentView === 'generatemeeting'} onClick={() => selectView('generatemeeting')} />
                <NavSubItem icon={<HowToReg />} label="My Interview Panel" active={currentView === 'mypanel'} onClick={() => selectView('mypanel')} />
              </NavGroup>

            {isIntern && (
              <NavItem icon={<School />} label="My Internship" active={currentView === 'myinternship'} onClick={() => selectView('myinternship')} />
            )}

            <NavDivider />

            {/* Profile */}
            {can('ViewEmployeeDetails') && (
              <NavItem icon={<Person />} label="My Details" active={currentView === 'mydetails'} onClick={() => selectView('mydetails')} />
            )}
            <NavItem icon={<Person />} label="Onboarding Form" active={currentView === 'onboarding'} onClick={() => selectView('onboarding')} />
            <NavItem icon={<Settings />} label="Settings" active={currentView === 'settings'} onClick={() => selectView('settings')} />
          </>
      );

  return (
    <AppShell
      panelTitle="Employee Panel"
      nav={drawerContent}
      mobileOpen={mobileOpen}
      onDrawerClose={onDrawerClose}
      onDrawerToggle={onDrawerToggle}
    >
      {renderContent()}
    </AppShell>
  );
};

export default DeveloperDashboard;
